import type { BaseServiceOptions, Pagination } from '@/core/types'
import {
  type TPaymentInvoice,
  paymentsInvoicesService,
  serviceConfig,
  TPaymentsInvoice,
} from '../../..'
import { useNotification, useServiceHandlers, defaultPagination } from '@/core'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { AxiosError } from 'axios'
import { toValue } from 'vue'

const { model } = serviceConfig.payments

export const useProvidePaymentsInvoiceQuery = (
  options: BaseServiceOptions<Pagination>,
) => {
  const { currentPage, enableQuery, filters, modelId, handlers } = options

  const { errorHttpNotification } = useNotification()
  const serviceHandlers = useServiceHandlers<TPaymentInvoice>({
    model,
    currentPage: toValue(currentPage) ?? 1,
    filterParams: filters?.loadRecords ?? '',
  })

  const deleteRecord = useMutation<void, AxiosError, TPaymentInvoice>(
    (payload) =>
      paymentsInvoicesService.deleteRecord({
        modelChildId: payload.id,
        modelId,
      }),
    {
      onSuccess: (_, payload) => serviceHandlers.deleteRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const editRecord = useMutation<TPaymentInvoice, AxiosError, TPaymentInvoice>(
    (payload: TPaymentInvoice) =>
      paymentsInvoicesService.editRecord(payload, {
        modelChildId: payload.id,
        modelId,
      }),
    {
      onSuccess: (payload) => serviceHandlers.updateRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const addRecord = useMutation<TPaymentInvoice, AxiosError, TPaymentInvoice>(
    (payload: TPaymentInvoice) =>
      paymentsInvoicesService.addRecord(payload, { modelId }),
    {
      onSuccess: (payload) => serviceHandlers.addRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const loadRecords = useQuery<TPaymentsInvoice, AxiosError>({
    queryKey: [model, currentPage, filters?.loadRecords],
    queryFn: () =>
      paymentsInvoicesService.loadRecords({
        dynamicFilters: filters?.loadRecords,
        page: currentPage,
        modelId,
      }),
    onError: (error) => errorHttpNotification(error),
    onSuccess: (data) => {
      if (data?.meta) {
        handlers?.onDataChange?.(data?.meta ?? defaultPagination)
      }
    },
    enabled: enableQuery?.loadRecords,
    refetchOnWindowFocus: false,
  })

  return {
    loadRecords,
    addRecord,
    editRecord,
    deleteRecord,
  }
}
